import React from 'react';
import { Accordion, Box, ButtonV2, DrawerV2, Flex, useDisclosure } from '@withjoy/joykit';
import { HotelTileList } from '../../components/Hotel';
import { HotelMap } from '../../components/Hotel/HotelMap';
import { HotelLoadingSkeleton } from '../../components/Hotel/HotelLoadingSkeleton';
import { useGuestEventHotelRoomBlocksQuery } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { pxToRem, styled } from '@withjoy/joykit/theme';
import { applyScrollBarStyles } from '@shared/utils/styles';
import { MapMarker } from '@withjoy/joykit/icons';
import { useMediaQuery } from '@withjoy/joykit/utils';

const ScrollableArea = styled(Flex)`
  ${applyScrollBarStyles};
  ::-webkit-scrollbar {
    width: 0;
  }
`;

interface Props {
  pageId: string;
  eventId: string;
  pageTitle?: string;
  subTitle?: string | null;
  isBrannanLayout?: boolean;
  isHotelCoupleNoteOpen?: boolean;
  handleCoupleNoteOpen?: () => void;
}

export const Custom: React.FC<Props> = ({ pageId, eventId, pageTitle, subTitle, isHotelCoupleNoteOpen, isBrannanLayout, handleCoupleNoteOpen }) => {
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, loading } = useGuestEventHotelRoomBlocksQuery({ variables: { eventId }, batchMode: 'fast' });
  const destination = data?.event?.hotelRoomBlocks?.destination || {};
  const reservedRoomBlocks = data?.event?.hotelRoomBlocks?.reservedRoomBlocks;
  const eventPhoto = data?.event?.photo?.url ?? '';

  const onSelectHotel = useEventCallback((id: string) => {
    if (isBrannanLayout) {
      onOpen();
    }
    // When click on marker, wait to open drawer first and then scroll to card
    setTimeout(() => {
      const scrollEl = document.getElementById(id);
      if (scrollEl) {
        scrollEl.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  });

  if (loading)
    return (
      <Flex width={'100%'} flexDirection={'column'}>
        <HotelLoadingSkeleton />
      </Flex>
    );

  return (
    <Flex
      flexDirection={isBrannanLayout && !isMobile ? 'row-reverse' : 'column'}
      justifyContent={'center'}
      alignItems={isBrannanLayout ? 'flex-start' : 'center'}
      width={'100%'}
      paddingY={{ _: 0, sm2: 7 }}
      paddingRight={{ _: 0, sm2: 7 }}
      height={isBrannanLayout && !isMobile ? pxToRem(864) : 'auto'}
      overflow={'hidden'}
    >
      {destination && reservedRoomBlocks && (
        <HotelMap destination={destination} reservedRoomBlocks={reservedRoomBlocks} pageTitle={pageTitle} onSelectHotel={onSelectHotel} isBrannanLayout={isBrannanLayout} />
      )}
      {/* Visible to Aloha Desktop & Mobile and Brannan Desktop */}
      {(!isBrannanLayout || !isMobile) && (
        <ScrollableArea
          width={isBrannanLayout && !isMobile ? pxToRem(400) : '100%'}
          flex={isBrannanLayout ? 'none' : 'auto'}
          flexDirection={'column'}
          paddingLeft={isBrannanLayout ? (isMobile ? 6 : 7) : 0}
          paddingRight={isBrannanLayout ? (isMobile ? 6 : 7) : 0}
          height={isBrannanLayout && !isMobile ? pxToRem(800) : 'auto'}
          overflowY={{ _: 'auto', sm2: 'scroll' }}
        >
          {reservedRoomBlocks && (
            <HotelTileList
              hotelTileListData={reservedRoomBlocks}
              destination={destination}
              eventPhoto={eventPhoto}
              handleCoupleNoteOpen={handleCoupleNoteOpen}
              isCoupleNoteOpen={isHotelCoupleNoteOpen}
            />
          )}
        </ScrollableArea>
      )}
      {/* Visible to Brannan Mobile Only */}
      {isBrannanLayout && isMobile && (
        <>
          <DrawerV2 isOpen={isOpen} onClose={onClose} anchor="bottom" size="auto" useBackdrop={false} overrides={{ Content: { props: { bottom: -76, boxShadow: 'none' } } }}>
            <DrawerV2.Body padding={0}>
              <Flex maxWidth={pxToRem(520)} justifyContent="center" paddingX={6} paddingY={7} backgroundColor="mono3" margin="auto">
                <Accordion>
                  <Accordion.Item>
                    <Accordion.Button
                      backgroundColor="mono3"
                      _hover={{ backgroundColor: 'mono3' }}
                      padding={0}
                      textAlign="center"
                      color="mono12"
                      fontWeight={600}
                      fontSize="22px"
                      lineHeight="28.6px"
                      fontFamily="Playfair Display"
                    >
                      <Box flex="1 1 0%">{pageTitle}</Box>
                      <Accordion.Icon />
                    </Accordion.Button>
                    <Accordion.Panel paddingX={0} paddingBottom={0} paddingTop={5} textAlign="center" fontSize={15} fontWeight={400} lineHeight={'28px'} fontFamily="Noto Serif">
                      {subTitle}
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </Flex>
              <Box whiteSpace="pre-wrap" paddingX={6} paddingBottom={pxToRem(160)}>
                {reservedRoomBlocks && (
                  <HotelTileList
                    hotelTileListData={reservedRoomBlocks}
                    destination={destination}
                    eventPhoto={eventPhoto}
                    handleCoupleNoteOpen={handleCoupleNoteOpen}
                    isCoupleNoteOpen={isHotelCoupleNoteOpen}
                  />
                )}
              </Box>
            </DrawerV2.Body>
          </DrawerV2>
          <Box
            paddingX={6}
            paddingY={5}
            width="100%"
            borderTopLeftRadius={4}
            borderTopRightRadius={4}
            backgroundColor="white"
            position="fixed"
            bottom={0}
            zIndex={1102}
            boxShadow="0px -7px 27px 0px rgba(44, 41, 37, 0.06), 0px -4px 16px 0px rgba(0, 0, 0, 0.07)"
          >
            <Box as="hr" role="separator" height={pxToRem(4)} width={pxToRem(40)} margin="auto" backgroundColor="#D5D5D5" border={0} borderRadius={pxToRem(6)} />
            <ButtonV2 intent="neutral" variant="ghost" onClick={onOpen} typographyVariant="button2" marginTop={5} fontWeight={600}>
              {reservedRoomBlocks?.length} {reservedRoomBlocks?.length === 1 ? 'hotel' : 'hotels'} available for our event
            </ButtonV2>
          </Box>
          {isOpen && (
            <Flex
              position="fixed"
              bottom={0}
              zIndex={1401}
              paddingY={6}
              width="100%"
              justifyContent="center"
              background="linear-gradient(180deg, rgba(255, 255, 255, 0) 0, #FFF 100%);"
            >
              <ButtonV2 intent="neutral" typographyVariant="button2" shape="rounded" backgroundColor="black" startIcon={<MapMarker />} onClick={onClose}>
                Map View
              </ButtonV2>
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};
